import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import type { Tone } from '@/lib/status'
import { cn } from '@/lib/utils'

export interface ScatterPoint {
  id: number
  x: number
  y: number
  /** Dot radius in px (the caller scales it, e.g. by keyword count). */
  r?: number
  /** Fill = the tone's soft colour, ring = its strong colour (a score band). */
  tone: Tone
  /** Short text inside the dot (initials). */
  text?: string
  /** 1–3 get a numbered badge. */
  rank?: number
}

export interface ScatterDomain {
  xMin: number
  xMax: number
  yMin: number
  yMax: number
}

export interface ScatterProps {
  points: ScatterPoint[]
  domain?: ScatterDomain
  xLabel: string
  yLabel: string
  /** Where the quadrant lines cross, when inside the domain. */
  center?: { x: number; y: number }
  /** Labels for the four corners: top-left, top-right, bottom-left, bottom-right. */
  quadrants?: [string, string, string, string]
  selectedId?: number | null
  onSelect?: (id: number | null) => void
  /** The hover card for a point. */
  tooltip?: (p: ScatterPoint) => ReactNode
  /** Read out for the whole chart. */
  label: string
  className?: string
}

/** SVG classes per tone. Literal so Tailwind sees every one. */
const DOT: Record<Tone, { fill: string; stroke: string; text: string }> = {
  neutral: { fill: 'fill-status-neutral-soft', stroke: 'stroke-status-neutral', text: 'fill-status-neutral' },
  review: { fill: 'fill-status-review-soft', stroke: 'stroke-status-review', text: 'fill-status-review' },
  scheduled: { fill: 'fill-status-scheduled-soft', stroke: 'stroke-status-scheduled', text: 'fill-status-scheduled' },
  publishing: { fill: 'fill-status-publishing-soft', stroke: 'stroke-status-publishing', text: 'fill-status-publishing' },
  published: { fill: 'fill-status-published-soft', stroke: 'stroke-status-published', text: 'fill-status-published' },
  failed: { fill: 'fill-status-failed-soft', stroke: 'stroke-status-failed', text: 'fill-status-failed' },
  deadline: { fill: 'fill-status-deadline-soft', stroke: 'stroke-status-deadline', text: 'fill-status-deadline' },
  web: { fill: 'fill-status-web-soft', stroke: 'stroke-status-web', text: 'fill-status-web' },
}

const FULL: ScatterDomain = { xMin: 0, xMax: 10, yMin: 0, yMax: 10 }
const PAD = 40

/** A domain around the points with 15% room, clamped to 0–10 — the "맞춤"
 *  zoom. */
export function fitDomain(points: { x: number; y: number }[]): ScatterDomain {
  if (points.length === 0) return FULL
  const xs = points.map((p) => p.x)
  const ys = points.map((p) => p.y)
  const [x0, x1, y0, y1] = [Math.min(...xs), Math.max(...xs), Math.min(...ys), Math.max(...ys)]
  const px = Math.max(0.5, (x1 - x0) * 0.15)
  const py = Math.max(0.5, (y1 - y0) * 0.15)
  return { xMin: Math.max(0, x0 - px), xMax: Math.min(10, x1 + px), yMin: Math.max(0, y0 - py), yMax: Math.min(10, y1 + py) }
}

function ticks(min: number, max: number): number[] {
  const step = max - min >= 5 ? 2 : max - min >= 2.5 ? 1 : 0.5
  const out: number[] = []
  for (let t = Math.ceil(min / step) * step; t <= max + 1e-9; t += step) out.push(Number(t.toFixed(1)))
  return out
}

/** A square scatter on one pair of axes (비교: market × ease). The page
 *  hides it below 768px and shows the table alone (plan S6). Dots are filled
 *  with their band's soft tone and ringed in its strong tone; the selected one
 *  gets an accent crosshair. Hover shows `tooltip`; a click selects. */
export function Scatter({
  points,
  domain = FULL,
  xLabel,
  yLabel,
  center = { x: 5, y: 5 },
  quadrants,
  selectedId = null,
  onSelect,
  tooltip,
  label,
  className,
}: ScatterProps) {
  const ref = useRef<HTMLDivElement>(null)
  const [size, setSize] = useState(560)
  const [hover, setHover] = useState<number | null>(null)

  useEffect(() => {
    const el = ref.current
    if (!el || typeof ResizeObserver === 'undefined') return
    const update = () => setSize(Math.max(320, Math.min(900, el.clientWidth || 560)))
    update()
    const ro = new ResizeObserver(update)
    ro.observe(el)
    return () => ro.disconnect()
  }, [])

  const d = domain
  const sx = (v: number) => PAD + ((v - d.xMin) / (d.xMax - d.xMin || 1)) * (size - 2 * PAD)
  const sy = (v: number) => size - PAD - ((v - d.yMin) / (d.yMax - d.yMin || 1)) * (size - 2 * PAD)
  const xt = useMemo(() => ticks(d.xMin, d.xMax), [d.xMin, d.xMax])
  const yt = useMemo(() => ticks(d.yMin, d.yMax), [d.yMin, d.yMax])
  const cx = center.x >= d.xMin && center.x <= d.xMax ? sx(center.x) : null
  const cy = center.y >= d.yMin && center.y <= d.yMax ? sy(center.y) : null
  const selected = points.find((p) => p.id === selectedId)
  const hovered = points.find((p) => p.id === hover)

  return (
    <div ref={ref} className={cn('relative w-full', className)}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} role="img" aria-label={label} className="block max-w-full" onClick={() => onSelect?.(null)}>
        {xt.map((t) => (
          <line key={`x${t}`} x1={sx(t)} x2={sx(t)} y1={PAD} y2={size - PAD} className="stroke-chart-grid" strokeWidth={1} />
        ))}
        {yt.map((t) => (
          <line key={`y${t}`} x1={PAD} x2={size - PAD} y1={sy(t)} y2={sy(t)} className="stroke-chart-grid" strokeWidth={1} />
        ))}
        {cx !== null && <line x1={cx} x2={cx} y1={PAD} y2={size - PAD} className="stroke-muted-foreground/40" strokeDasharray="4 4" />}
        {cy !== null && <line x1={PAD} x2={size - PAD} y1={cy} y2={cy} className="stroke-muted-foreground/40" strokeDasharray="4 4" />}
        {xt.map((t) => (
          <text key={`xl${t}`} x={sx(t)} y={size - PAD + 16} textAnchor="middle" className="fill-muted-foreground text-[11px] tabular-nums">
            {t}
          </text>
        ))}
        {yt.map((t) => (
          <text key={`yl${t}`} x={PAD - 8} y={sy(t) + 4} textAnchor="end" className="fill-muted-foreground text-[11px] tabular-nums">
            {t}
          </text>
        ))}
        <text x={size / 2} y={size - 6} textAnchor="middle" className="fill-muted-foreground text-[12px]">
          {xLabel}
        </text>
        <text x={12} y={size / 2} textAnchor="middle" transform={`rotate(-90 12 ${size / 2})`} className="fill-muted-foreground text-[12px]">
          {yLabel}
        </text>
        {quadrants && (
          <>
            <text x={PAD + 8} y={PAD + 16} className="fill-muted-foreground text-[11px]">{quadrants[0]}</text>
            <text x={size - PAD - 8} y={PAD + 16} textAnchor="end" className="fill-muted-foreground text-[11px]">{quadrants[1]}</text>
            <text x={PAD + 8} y={size - PAD - 8} className="fill-muted-foreground text-[11px]">{quadrants[2]}</text>
            <text x={size - PAD - 8} y={size - PAD - 8} textAnchor="end" className="fill-muted-foreground text-[11px]">{quadrants[3]}</text>
          </>
        )}
        {selected && (
          <g aria-hidden>
            <line x1={sx(selected.x)} x2={sx(selected.x)} y1={PAD} y2={size - PAD} className="stroke-accent" strokeWidth={1} />
            <line x1={PAD} x2={size - PAD} y1={sy(selected.y)} y2={sy(selected.y)} className="stroke-accent" strokeWidth={1} />
          </g>
        )}
        {points.map((p) => {
          const r = p.r ?? 12
          const on = p.id === selectedId || p.id === hover
          const tone = DOT[p.tone]
          return (
            <g
              key={p.id}
              transform={`translate(${sx(p.x)} ${sy(p.y)})`}
              onMouseEnter={() => setHover(p.id)}
              onMouseLeave={() => setHover((h) => (h === p.id ? null : h))}
              onClick={(e) => {
                e.stopPropagation()
                onSelect?.(p.id === selectedId ? null : p.id)
              }}
              className="cursor-pointer"
              data-testid={`scatter-point-${p.id}`}
            >
              {/* A bigger invisible target than the dot. */}
              <circle r={Math.max(r, 14)} fill="transparent" />
              {/* A 2px ring in the card colour keeps overlapping dots apart. */}
              <circle r={r + 1} className="fill-card" />
              <circle r={r} className={cn(tone.fill, tone.stroke)} strokeWidth={on ? 2.5 : 1.5} />
              {p.text && (
                <text textAnchor="middle" dy="0.35em" className={cn('pointer-events-none text-[10px] font-semibold', tone.text)}>
                  {p.text}
                </text>
              )}
              {p.rank !== undefined && p.rank >= 1 && p.rank <= 3 && (
                <g transform={`translate(${r * 0.75} ${-r * 0.75})`}>
                  <circle r={7} className="fill-foreground stroke-card" strokeWidth={1.5} />
                  <text textAnchor="middle" dy="0.35em" className="pointer-events-none fill-background text-[9px] font-bold">
                    {p.rank}
                  </text>
                </g>
              )}
            </g>
          )
        })}
      </svg>
      {hovered && tooltip && (
        <div
          role="tooltip"
          className="pointer-events-none absolute z-10 w-56 -translate-x-1/2 rounded-md border border-border bg-card p-3 text-meta shadow-overlay"
          style={{ left: Math.min(size - 112, Math.max(112, sx(hovered.x))), top: sy(hovered.y) + (hovered.r ?? 12) + 8 }}
        >
          {tooltip(hovered)}
        </div>
      )}
    </div>
  )
}
